import { useState, useEffect } from 'react'
import { getSiteHot, stats, ApiError, translateError, type SiteHot } from '../lib/api'
import { useAuth } from '../lib/store'
import { Table } from '../components/Table'

export default function Module4() {
  // (useAuth 不再需要 — 移除 module4 开关后,本页不再需要 admin token)
  // const { token: _token } = useAuth(); void _token  // 注释保留: 之前用 token 拉 module4 状态

  // 倒计时(到 2026-06-25 12:00:00 +08:00)
  const [now, setNow] = useState<number>(Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  const target = new Date('2026-06-25T12:00:00+08:00').getTime()
  const diff = Math.max(0, target - now)
  const d = Math.floor(diff / 86400000)
  const h = Math.floor((diff % 86400000) / 3600000)
  const m = Math.floor((diff % 3600000) / 60000)
  const s = Math.floor((diff % 60000) / 1000)
  const isLive = now >= target

  // 实时热度(/api/site/hot,从 events 表聚合)
  const [hot, setHot] = useState<SiteHot | null>(null)
  const [hotErr, setHotErr] = useState<string | null>(null)
  const [hotLoading, setHotLoading] = useState(false)
  useEffect(() => {
    if (hot || hotLoading || hotErr) return
    setHotLoading(true)
    getSiteHot()
      .then(r => setHot(r))
      .catch(e => setHotErr(e instanceof ApiError ? translateError(e.code) : String((e as Error).message)))
      .finally(() => setHotLoading(false))
  }, [hot, hotLoading, hotErr])

  // 去年热度预演(去年 admissions 数据,跟 events 无关,作为历史背景)
  const [hotRows, setHotRows] = useState<any[] | null>(null)
  const [lastYearErr, setLastYearErr] = useState<string | null>(null)
  const [lastYearLoading, setLastYearLoading] = useState(false)
  useEffect(() => {
    if (hotRows || lastYearLoading || lastYearErr) return
    setLastYearLoading(true)
    stats.hotMajors({ year: 2025, curriculum: '物理类', offset: 0 })
      .then((r: any) => setHotRows((r?.rows || []).slice(0, 10)))
      .catch(e => setLastYearErr(e instanceof ApiError ? translateError(e.code) : String((e as Error).message)))
      .finally(() => setLastYearLoading(false))
  }, [hotRows, lastYearLoading, lastYearErr])

  // admin 状态卡已删除 — 热度始终开放,不再需要 enabled 检查
  const hasRealtime = !!(hot && (hot.hot_schools_24h.length > 0 || hot.hot_majors_7d.length > 0))

  return (
    <div>
      <h2 className="serif" style={{ fontSize: 'var(--fs-h1)', fontWeight: 600, lineHeight: 1.15, margin: '0 0 .5rem', display: 'flex', alignItems: 'center', gap: '.5rem' }}>
        <span className="module-badge">04</span>实时热度
        <ScannerIcon />
      </h2>
      <p style={{ color: 'var(--ink2)', fontSize: 'var(--fs-body)', marginBottom: '1.25rem' }}>大家在查什么学校 /专业 / 同分去向。出分后完整榜单自动开放。</p>

      {/* 1. 倒计时 — 所有用户可见 */}
      <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
        <div style={{ fontSize: 'var(--fs-body)', color: 'var(--ink2)', marginBottom: '.75rem' }}>
          {isLive ? '出分啦 · 实时数据已开放' : '距 6/25 12:00 出分还有'}
        </div>
        {!isLive && (
          <div className="mono" style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
            {(() => {
              // 4 个单位,每个环显示该单位的进度(秒环从 59 倒数,分环 0-59,时环 0-23,天环到目标日)
              const units: Array<{ v: number; l: string; max: number }> = [
                { v: d, l: '天', max: 0 },     // 天不画环,只画数字(到目标日的进度另算)
                { v: h, l: '时', max: 24 },
                { v: m, l: '分', max: 60 },
                { v: s, l: '秒', max: 60 },
              ]
              return units.map(({ v, l, max }) => (
                <RadialCountdown key={l} v={v} l={l} max={max} />
              ))
            })()}
          </div>
        )}
      </div>

      {/* 2. 实时热度 — 从 events 表聚合,2 块数据(24h 搜 / 7d 看) */}
      <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
        <div style={{ fontWeight: 600, fontSize: 'var(--fs-body)', marginBottom: '.5rem', display: 'flex', alignItems: 'center', gap: '.5rem' }}>
          <span className="dot-live" style={{ width: 6, height: 6 }} />
          实时热度
          <span style={{ fontWeight: 400, fontSize: 'var(--fs-capt)', color: 'var(--ink3)' }}>· 由本系统用户行为聚合</span>
        </div>
        {/* 7d 活跃 sparkline — 7 个 mini bar,今天高亮朱砂,贴在 实时热度 头部底下 */}
        {hot && hot.last_7d_daily && <Sparkline data={hot.last_7d_daily} />}
        {hotLoading && <><div style={{ fontSize: 'var(--fs-body)', color: 'var(--ink3)', marginBottom: '.25rem' }}>聚合中…</div><div className="progress-bar" style={{marginTop:0,marginBottom:'.5rem'}} /></>}
        {hotErr && <div className="alert-err">{hotErr}</div>}
        {hot && !hasRealtime && (
          <div style={{ fontSize: 'var(--fs-body)', color: 'var(--ink3)' }}>
            暂无排行数据。试试在搜索框输学校 / 专业,或在列表里点几个'查看' → 你的查询会开始累计。
          </div>
        )}
        {hot && hot.hot_schools_24h.length > 0 && (
          <div style={{ marginBottom: '1.25rem' }}>
            <div className="eyebrow" style={{ marginBottom: '.5rem', display: 'flex', alignItems: 'center', gap: '.375rem' }}>
              <SchoolBuildingIcon /> 近 24h 搜索最多的学校
            </div>
            <BarChart items={hot.hot_schools_24h} unit="次搜索" />
          </div>
        )}
        {hot && hot.hot_majors_7d.length > 0 && (
          <div>
            <div className="eyebrow" style={{ marginBottom: '.5rem', display: 'flex', alignItems: 'center', gap: '.375rem' }}>
              <BookIcon /> 近 7d 查询最多的专业
            </div>
            <BarChart items={hot.hot_majors_7d} unit="次查询" />
          </div>
        )}
        {/* 数据来源说明 — 合并到实时热度卡片底部(替代之前的'实时数据'独立卡) */}
        {hasRealtime && (
          <div style={{ fontSize: 'var(--fs-capt)', color: 'var(--ink3)', marginTop: '1rem', borderTop: '1px dashed var(--border)', paddingTop: '.5rem' }}>
            用户行为实时聚合 · 每 3 秒滚动累积 · 无人工干预
          </div>
        )}
      </div>

      {/* 3. 去年 Top 10 — 历史背景,跟 events 无关,跟 hotMajors() 静态数据 */}
      <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
        <div style={{ fontWeight: 600, fontSize: 'var(--fs-body)', marginBottom: '.5rem' }}>去年(2025)物理类考生最关注 Top 10 专业</div>
        {lastYearLoading && <><div style={{ fontSize: 'var(--fs-body)', color: 'var(--ink3)', marginBottom: '.25rem' }}>查 5 年录取中…</div><div className="progress-bar" style={{marginTop:0,marginBottom:'.5rem'}} /></>}
        {lastYearErr && <div className="alert-err">{lastYearErr}</div>}
        {hotRows && (
          <>
            <Table
              rows={hotRows}
              cols={[
                { k: 'major_name', label: '专业' },
                { k: 'school_count', label: '开设校数', mono: true },
                { k: 'best_school_name', label: '最强校' },
                { k: 'best_min_score', label: '该校最高分', mono: true },
                { k: 'avg_min_score', label: '平均分', mono: true },
              ]}
            />
             <div style={{ fontSize: 'var(--fs-capt)', color: 'var(--ink3)', marginTop: '.5rem' }}>今年数据 6/25 12:00 自动更新。</div>
           </>
         )}
       </div>

    </div>
  )
}

/**
 * 雷达 / scanner SVG 图标 — 跟 实时热度 主题匹配
 * 12×12 viewBox,正方形,跟 module-badge 视觉权重相当
 */
function ScannerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'var(--ink3)', flexShrink: 0 }} aria-hidden="true">
      {/* 同心方框 — 像 radar scan */}
      <rect x="2.5" y="2.5" width="19" height="19" />
      <rect x="6.5" y="6.5" width="11" height="11" strokeDasharray="2 2" />
      <rect x="10.5" y="10.5" width="3" height="3" fill="currentColor" />
      {/* 4 角加 L 形 brackets */}
      <path d="M2.5 7 L2.5 2.5 L7 2.5" />
      <path d="M21.5 7 L21.5 2.5 L17 2.5" />
      <path d="M2.5 17 L2.5 21.5 L7 21.5" />
      <path d="M21.5 17 L21.5 21.5 L17 21.5" />
    </svg>
  )
}

/**
 * 倒计时径向进度环 — SVG circle stroke-dasharray 实现
 * 中心是数字,环显示该单位的进度(秒 0-60、分 0-60、时 0-24)
 * 天不画环(只画大数字,因为"到出分还有 N 天"是离散值,无连续进度可言)
 */
function RadialCountdown({ v, l, max }: { v: number; l: string; max: number }) {
  if (max === 0) {
    // 天:只画大数字,无环
    return (
      <div style={{ textAlign: 'center', minWidth: 64 }}>
        <div className="serif" style={{ fontSize: '2.5rem', fontWeight: 600, lineHeight: 1, color: 'var(--ink)' }}>{v}</div>
        <div style={{ fontSize: 'var(--fs-capt)', color: 'var(--ink3)', marginTop: '.25rem', letterSpacing: '.15em' }}>天</div>
      </div>
    )
  }
  // 时/分/秒:画 64×64 SVG 环
  const size = 64
  const stroke = 4
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  // 进度:从满(m-1)递减到 0 → 用 (max - v) / max 算
  const progress = Math.max(0, Math.min(1, (max - v) / max))
  const offset = c * (1 - progress)
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          {/* 底环:浅灰 */}
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border)" strokeWidth={stroke} />
          {/* 进度环:朱砂色,逆时针递减 */}
          <circle cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke="var(--brand)" strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 600ms ease-out' }} />
        </svg>
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.5rem', fontWeight: 600, color: 'var(--ink)',
          fontFamily: 'var(--mono)', letterSpacing: '-0.02em',
        }}>{String(v).padStart(2, '0')}</div>
      </div>
      <div style={{ fontSize: 'var(--fs-capt)', color: 'var(--ink3)', marginTop: '.25rem', letterSpacing: '.15em' }}>{l}</div>
    </div>
  )
}

/**
 * 排行条形图 — 替换默认的 <Table> 渲染
 * 每行:排名徽章 [01] + 名称 + 渐变条(宽度 = n / max * 100%) + 数字
 * #01 数字用 var(--brand) 朱砂色,其余用 var(--ink2)
 * 渐变条: var(--brand) → var(--ink2) 从左到右 fade
 */
function BarChart({ items, unit }: { items: Array<{ name: string; n: number }>; unit: string }) {
  const max = Math.max(1, ...items.map(r => r.n))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
      {items.map((r, i) => {
        const rank = i + 1
        const pct = (r.n / max) * 100
        const isTop = rank === 1
        return (
          <div key={r.name} style={{ display: 'grid', gridTemplateColumns: '32px 1fr 60px', gap: '.5rem', alignItems: 'center' }}>
            {/* 排名徽章 — #01 朱砂,其他墨色 */}
            <span className="mono" style={{
              fontSize: 'var(--fs-capt)', fontWeight: 600,
              color: isTop ? 'var(--brand)' : 'var(--ink3)',
              textAlign: 'center',
            }}>{String(rank).padStart(2, '0')}</span>
            {/* 名称 + 渐变条 */}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 'var(--fs-body)', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '4px' }}>{r.name}</div>
              <div style={{ position: 'relative', height: 6, background: 'var(--bg-deep)', borderRadius: 1, overflow: 'hidden' }}>
                <div style={{
                  position: 'absolute', top: 0, left: 0, bottom: 0,
                  width: `${pct}%`,
                  background: 'linear-gradient(to right, var(--brand), var(--ink2))',
                  borderRadius: 1,
                  transition: 'width 600ms ease-out',
                }} />
              </div>
            </div>
            {/* 数字 + 单位 */}
            <div style={{ textAlign: 'right' }}>
              <div className="mono" style={{ fontSize: 'var(--fs-body)', fontWeight: 600, color: isTop ? 'var(--brand)' : 'var(--ink2)' }}>{r.n}</div>
              <div style={{ fontSize: 'var(--fs-capt)', color: 'var(--ink3)' }}>{unit}</div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** 学校建筑 SVG 图标(14×14) */
function SchoolBuildingIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ color: 'var(--ink3)' }} aria-hidden="true">
      <path d="M2 13 L2 6 L7 3 L12 6 L12 13 Z" />
      <path d="M5 13 L5 9 L9 9 L9 13" />
      <path d="M2 13 L12 13" strokeWidth="1.5" />
    </svg>
  )
}

/** 书本 SVG 图标(14×14) */
function BookIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ color: 'var(--ink3)' }} aria-hidden="true">
      <path d="M2 2 L7 3 L7 12 L2 11 Z" />
      <path d="M12 2 L7 3 L7 12 L12 11 Z" />
      <path d="M7 3 L7 12" />
    </svg>
  )
}

/**
 * 7d 活跃 sparkline — 7 个 mini bar 拼成柱状图
 * 今天(index 6)用 var(--brand) 朱砂高亮,其余 var(--ink3)
 * 高度 32px,bar 间 3px
 * 在 bar 顶端显示数字(可选,只在非 0 时显示,避免噪声)
 */
function Sparkline({ data }: { data: number[] }) {
  const max = Math.max(1, ...data)
  const total = data.reduce((a, b) => a + b, 0)
  const todayIdx = data.length - 1
  const todayVal = data[todayIdx] ?? 0
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
      gap: 4, padding: '.5rem 0 .25rem', marginBottom: '1rem',
      borderBottom: '1px dashed var(--border)',
    }} title={`近 7d 每日事件数(右为今天)${total > 0 ? ` · 合计 ${total}` : ''}`}>
      {data.map((n, i) => {
        const pct = (n / max) * 100
        const isToday = i === todayIdx
        return (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: 1 }}>
            <div style={{
              width: '100%', maxWidth: 36,
              height: `${Math.max(3, pct)}%`, minHeight: 3,
              background: isToday ? 'var(--brand)' : 'var(--ink3)',
              borderRadius: 1,
              transition: 'height 400ms ease-out',
              position: 'relative',
            }}>
              {n > 0 && (
                <div className="mono" style={{
                  position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                  fontSize: 10, color: isToday ? 'var(--brand)' : 'var(--ink3)', fontWeight: 600,
                }}>{n}</div>
              )}
            </div>
            <div style={{ fontSize: 9, color: 'var(--ink3)', fontFamily: 'var(--mono)' }}>{isToday ? '今' : i === todayIdx - 1 ? '昨' : `${todayIdx - i}d`}</div>
          </div>
        )
      })}
    </div>
  )
}
